import type { PublicPlanItem } from '@practium/features/api';
import { formatIntervalAdjective, formatPlanPriceBrl } from '../planFormatters';
import styles from '../checkoutShared.module.css';

export type CheckoutPaymentMethod = 'credit_card' | 'pix';

const PIX_CHECKOUT_ENABLED = false;

interface StepPaymentProps {
	plan: PublicPlanItem;
	onChangePlan: () => void;
	onSubmit: () => void;
	isSubmitting: boolean;
	canSubmit: boolean;
	paymentMethod: CheckoutPaymentMethod;
	onChangePaymentMethod: (method: CheckoutPaymentMethod) => void;
}

const METHOD_LABELS: Record<CheckoutPaymentMethod, string> = {
	credit_card: 'Cartão de Crédito',
	pix: 'PIX',
};

export const StepPayment = ({
	plan,
	onChangePlan,
	onSubmit,
	isSubmitting,
	canSubmit,
	paymentMethod,
	onChangePaymentMethod,
}: StepPaymentProps) => {
	const priceLabel = formatPlanPriceBrl(plan.plan.price);
	const intervalAdjective = formatIntervalAdjective(plan.plan.interval, plan.plan.intervalCount);
	const availableMethods = plan.plan.paymentMethods.filter(
		(method): method is CheckoutPaymentMethod =>
			method === 'credit_card' || (method === 'pix' && PIX_CHECKOUT_ENABLED)
	);
	const methods = availableMethods.length > 0 ? availableMethods : ['credit_card' as const];

	return (
		<div className={styles.summaryCard}>
			<p className={styles.summaryTitle}>Resumo do Pedido</p>

			<div className={styles.orderRow}>
				<span className={styles.orderName}>
					{plan.plan.name} . {intervalAdjective}
				</span>
				<div className={styles.orderRight}>
					<span className={styles.orderPrice}>R$ {priceLabel}</span>
					<button type="button" className={styles.planChangeLink} onClick={onChangePlan}>
						[Trocar plano]
					</button>
				</div>
			</div>

			<div className={styles.summaryDivider} />

			<div className={styles.totalRow}>
				<span className={styles.totalLabelBig}>Total:</span>
				<span className={styles.totalValueBig}>R$ {priceLabel}</span>
			</div>

			<div className={styles.methodOptions}>
				{methods.map((method) => (
					<button
						key={method}
						type="button"
						className={styles.methodRow}
						onClick={() => onChangePaymentMethod(method)}
					>
						<div className={styles.methodLeft}>
							<div className={`${styles.methodRadio} ${paymentMethod === method ? styles.selected : ''}`}>
								{paymentMethod === method && <div className={styles.methodRadioDot} />}
							</div>
							<span className={styles.methodLabel}>{METHOD_LABELS[method]}</span>
						</div>
						{method === 'credit_card' ? (
							<div className={styles.cardBrandIcons}>
								<img src="/cards/visa.png" alt="Visa" className={styles.cardBrandIcon} />
								<img
									src="/cards/MasterCard_early_1990s_logo.png"
									alt="Mastercard"
									className={styles.cardBrandIcon}
								/>
								<img src="/cards/elo-3.svg" alt="Elo" className={styles.cardBrandIcon} />
							</div>
						) : (
							<div className={styles.cardBrandIcons}>
								<img src="/cards/pix.png" alt="PIX" className={styles.cardBrandIcon} />
							</div>
						)}
					</button>
				))}
			</div>

			<button
				type="button"
				className={styles.submitButton}
				onClick={onSubmit}
				disabled={!canSubmit || isSubmitting}
			>
				{isSubmitting ? 'Redirecionando...' : 'Ir para Pagamento'}
			</button>
		</div>
	);
};
